'use client'

import { useEffect, useRef } from 'react'
import { usePathname } from 'next/navigation'
import { getSession } from 'next-auth/react'
import { isPublicPath } from '@/lib/auth/routes'
import { hardNavigate } from '@/lib/browser/navigate'

/**
 * Redirects to /login when the tab regains focus and the session has expired.
 *
 * JWT sessions expire after a fixed maxAge with no refresh (see auth.ts), and
 * nothing on the client notices once the token dies: a tab left open past expiry
 * keeps rendering while every /api call silently 401s. This guard re-checks the
 * session on focus / visibility change and sends the user to /login as soon as it
 * is no longer valid. Public paths (login, setup-password) are left untouched.
 */
export function SessionExpiryGuard() {
  const pathname = usePathname()
  const checking = useRef(false)

  useEffect(() => {
    if (isPublicPath(pathname)) return

    async function check() {
      if (checking.current) return
      if (document.visibilityState !== 'visible') return
      checking.current = true
      try {
        const session = await getSession()
        if (!session?.user) {
          hardNavigate('/login')
        }
      } finally {
        checking.current = false
      }
    }

    window.addEventListener('focus', check)
    document.addEventListener('visibilitychange', check)
    return () => {
      window.removeEventListener('focus', check)
      document.removeEventListener('visibilitychange', check)
    }
  }, [pathname])

  return null
}
